import { create } from 'zustand';
import { supabase, DbCampaignInvite } from '../lib/supabase';

/**
 * Campaign Invite Store
 *
 * Manages invite state for both DMs (creating/managing invites)
 * and players (viewing/accepting invites).
 */

// =============================================================================
// TYPES
// =============================================================================

export interface CampaignInvite {
  id: string;
  campaignId: string;
  code: string;
  email: string | null;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  expiresAt: string | null;
  maxUses: number | null;
  useCount: number;
  createdAt: string;
}

export interface InvitePreview {
  valid: boolean;
  reason?: string;
  campaignId?: string;
  campaignName?: string;
  campaignDescription?: string | null;
  dmName?: string;
  playerCount?: number;
}

/**
 * Shape of a row returned by the `machina_get_invite(p_code)` SECURITY DEFINER
 * RPC (snake_case, matching the SQL `returns table(...)` definition).
 */
interface InviteRpcRow {
  valid: boolean;
  reason: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  campaign_description: string | null;
  dm_name: string | null;
  player_count: number | string | null;
}

interface InviteState {
  // DM side: invites for their campaigns
  campaignInvites: CampaignInvite[];
  isLoadingInvites: boolean;
  invitesError: string | null;

  // Player side: invite preview
  invitePreview: InvitePreview | null;
  isLoadingPreview: boolean;
  previewError: string | null;

  // Accept state
  isAccepting: boolean;
  acceptError: string | null;

  // DM Actions
  loadCampaignInvites: (campaignId: string) => Promise<void>;
  createInvite: (
    campaignId: string,
    userId: string,
    options?: {
      expiresInHours?: number;
      maxUses?: number;
      email?: string;
    }
  ) => Promise<{ code: string; id: string } | null>;
  revokeInvite: (inviteId: string) => Promise<boolean>;

  // Player Actions
  loadInvitePreview: (code: string) => Promise<void>;
  acceptInvite: (code: string, userId: string) => Promise<{ success: boolean; campaignId?: string; alreadyMember?: boolean }>;
  clearPreview: () => void;
}

// =============================================================================
// HELPERS
// =============================================================================

function dbToInvite(db: DbCampaignInvite): CampaignInvite {
  return {
    id: db.id,
    campaignId: db.campaign_id,
    code: db.code,
    email: db.email,
    status: db.status,
    expiresAt: db.expires_at,
    maxUses: db.max_uses,
    useCount: db.use_count,
    createdAt: db.created_at,
  };
}

/**
 * Generate invite code (8 chars, no ambiguous characters).
 *
 * Uses the Web Crypto API (`crypto.getRandomValues`) instead of `Math.random`
 * so codes are not predictable from the JS PRNG state. Rejection sampling
 * discards values in the biased tail of the uint32 range so every character
 * in the alphabet is equally likely (no modulo bias).
 */
function generateInviteCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const len = chars.length;
  // Largest multiple of `len` that fits in a uint32; values >= this would
  // skew the distribution toward lower indices, so we reject them.
  const limit = Math.floor(0x100000000 / len) * len;
  let code = '';
  const buf = new Uint32Array(1);
  while (code.length < 8) {
    crypto.getRandomValues(buf);
    const r = buf[0];
    if (r >= limit) continue; // rejection sample to avoid modulo bias
    code += chars[r % len];
  }
  return code;
}

/**
 * Build invite URL for sharing
 */
export function getInviteUrl(code: string): string {
  const baseUrl = window.location.origin;
  return `${baseUrl}/#join/${code}`;
}

// =============================================================================
// STORE
// =============================================================================

export const useInviteStore = create<InviteState>()((set) => ({
  // Initial state
  campaignInvites: [],
  isLoadingInvites: false,
  invitesError: null,

  invitePreview: null,
  isLoadingPreview: false,
  previewError: null,

  isAccepting: false,
  acceptError: null,

  // =========================================================================
  // DM ACTIONS
  // =========================================================================

  loadCampaignInvites: async (campaignId: string) => {
    set({ isLoadingInvites: true, invitesError: null });

    try {
      const { data, error } = await supabase
        .from('machina_campaign_invites')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const invites = (data || []).map((d) => dbToInvite(d as DbCampaignInvite));
      set({ campaignInvites: invites, isLoadingInvites: false });
    } catch (err) {
      console.error('[InviteStore] Failed to load invites:', err);
      set({
        invitesError: err instanceof Error ? err.message : 'Failed to load invites',
        isLoadingInvites: false,
      });
    }
  },

  createInvite: async (campaignId, userId, options = {}) => {
    set({ invitesError: null });

    try {
      const code = generateInviteCode();
      const expiresAt = options.expiresInHours
        ? new Date(Date.now() + options.expiresInHours * 60 * 60 * 1000).toISOString()
        : null;

      const { data, error } = await supabase
        .from('machina_campaign_invites')
        .insert({
          campaign_id: campaignId,
          code,
          email: options.email || null,
          created_by: userId,
          expires_at: expiresAt,
          max_uses: options.maxUses ?? 1,
        })
        .select()
        .single();

      if (error) throw error;

      // Add to local state
      const newInvite = dbToInvite(data as DbCampaignInvite);
      set((state) => ({
        campaignInvites: [newInvite, ...state.campaignInvites],
      }));

      return { code: newInvite.code, id: newInvite.id };
    } catch (err) {
      console.error('[InviteStore] Failed to create invite:', err);
      set({
        invitesError: err instanceof Error ? err.message : 'Failed to create invite',
      });
      return null;
    }
  },

  revokeInvite: async (inviteId: string) => {
    set({ invitesError: null });

    try {
      const { error } = await supabase
        .from('machina_campaign_invites')
        .update({ status: 'revoked' })
        .eq('id', inviteId);

      if (error) throw error;

      // Update local state
      set((state) => ({
        campaignInvites: state.campaignInvites.map((inv) =>
          inv.id === inviteId ? { ...inv, status: 'revoked' as const } : inv
        ),
      }));

      return true;
    } catch (err) {
      console.error('[InviteStore] Failed to revoke invite:', err);
      set({
        invitesError: err instanceof Error ? err.message : 'Failed to revoke invite',
      });
      return false;
    }
  },

  // =========================================================================
  // PLAYER ACTIONS
  // =========================================================================

  loadInvitePreview: async (code: string) => {
    set({ isLoadingPreview: true, previewError: null, invitePreview: null });

    try {
      // machina_campaign_invites is locked to the DM under deny-by-default RLS;
      // a joining (non-member) player previews through the SECURITY DEFINER RPC,
      // which returns ONLY safe fields (validity, campaign name/description, DM
      // name, player count) — never the raw invite row.
      const { data, error } = await supabase.rpc('machina_get_invite', {
        p_code: code.trim().toUpperCase(),
      });

      if (error) throw error;

      // The RPC returns a set (table) — take the first row.
      const row = (Array.isArray(data) ? data[0] : data) as InviteRpcRow | undefined;

      if (!row) {
        set({
          invitePreview: { valid: false, reason: 'Invite not found' },
          isLoadingPreview: false,
        });
        return;
      }

      if (!row.valid) {
        set({
          invitePreview: { valid: false, reason: row.reason || 'Invalid invitation' },
          isLoadingPreview: false,
        });
        return;
      }

      set({
        invitePreview: {
          valid: true,
          campaignId: row.campaign_id ?? undefined,
          campaignName: row.campaign_name ?? undefined,
          campaignDescription: row.campaign_description ?? null,
          dmName: row.dm_name ?? 'Unknown DM',
          playerCount: Number(row.player_count ?? 0),
        },
        isLoadingPreview: false,
      });
    } catch (err) {
      console.error('[InviteStore] Failed to load invite preview:', err);
      set({
        previewError: err instanceof Error ? err.message : 'Failed to load invite',
        isLoadingPreview: false,
      });
    }
  },

  acceptInvite: async (code: string, _userId: string) => {
    set({ isAccepting: true, acceptError: null });

    try {
      // Single atomic SECURITY DEFINER RPC: validates the invite, self-joins
      // machina_campaign_members (on conflict do nothing — idempotent re-accept),
      // bumps use_count, and returns the campaign_id. The joining uid is derived
      // from auth.uid() server-side. The old direct invite read + member insert +
      // use_count update are all denied by deny-by-default RLS now.
      const { data, error } = await supabase.rpc('machina_accept_invite', {
        p_code: code.trim().toUpperCase(),
      });

      if (error) throw error;

      const campaignId = (typeof data === 'string' ? data : undefined) as string | undefined;
      if (!campaignId) {
        throw new Error('Failed to join campaign');
      }

      set({ isAccepting: false });
      // The RPC is idempotent for existing members; we no longer distinguish
      // already-member here (JoinPage treats both as a successful join).
      return { success: true, campaignId, alreadyMember: false };
    } catch (err) {
      console.error('[InviteStore] Failed to accept invite:', err);
      set({
        acceptError: err instanceof Error ? err.message : 'Failed to accept invite',
        isAccepting: false,
      });
      return { success: false };
    }
  },

  clearPreview: () => {
    set({
      invitePreview: null,
      isLoadingPreview: false,
      previewError: null,
      acceptError: null,
    });
  },
}));
